import { useId } from 'react';
import { Stack, Text } from '@mantine/core';
import { IconFlask } from '@tabler/icons-react';
import { Link, useRouterState } from '@tanstack/react-router';
import { bareList } from '~portal/components/common/common.css';
import { useRestrictedAccess } from '~portal/hooks';
import { Cmp } from '~portal/lib';
import * as styles from './GlobalNav.css';

/**
 * The Restricted section's destinations (round 3, lane 11, H2-314). One entry today; later
 * staging-only pages join this list rather than the five product sections, which mirror Figma.
 */
export const restrictedItems = [
  { label: 'Experiments', to: '/restricted/experiments', Icon: IconFlask },
] as const;

export type RestrictedNavGroupProps = {
  /** Called when an item is chosen — the drawer closes itself with it. */
  onNavigate?: () => void;
};

/**
 * @desc The "Restricted" group under the section pills: staging-only, admin-only pages for
 * reviewing prototypes. Renders nothing unless `useRestrictedAccess` holds (flag on, platform
 * build, admin claims), so production — where the flag is unset — never draws it. Its own labelled
 * list, apart from the five sections, so it never reads as a product section.
 */
export const RestrictedNavGroup: Cmp<RestrictedNavGroupProps> = function ({ onNavigate }) {
  const visible = useRestrictedAccess();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const headingId = useId();

  if (!visible) return null;

  return (
    <Stack gap="xs" mt="xl" data-testid="restricted-nav">
      <Text id={headingId} size="xs" fw={600} tt="uppercase" c="dimmed" px="md">
        Restricted
      </Text>
      <Stack component="ul" gap="md" aria-labelledby={headingId} className={bareList}>
        {restrictedItems.map((item) => {
          const active = path === item.to || path.startsWith(`${item.to}/`);
          return (
            <li key={item.to}>
              <Link
                to={item.to}
                className={styles.item}
                data-selected={active || undefined}
                onClick={onNavigate}
              >
                <item.Icon size={22} stroke={1.5} aria-hidden />
                {item.label}
              </Link>
            </li>
          );
        })}
      </Stack>
    </Stack>
  );
};
